import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaShoppingCart, FaBox, FaHourglassHalf, FaTruck } from "react-icons/fa";
import StatBox from "../components/dashboardcomponents/StatBox";
import SalesGraph from "../components/dashboardcomponents/SalesGraph";
import { baseurl } from "../config/url";
import LowStockTable from "../components/dashboardcomponents/LowStock";
import RecentOrders from "../components/dashboardcomponents/RecentOrders";
import Loader from "../components/Loader";

function Dashboard() {
  let root = JSON.parse(localStorage.getItem("root") || "{}");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [deliveredCount, setDeliveredCount] = useState(0);
  const [orders, setorders] = useState([]);
  const [error, seterror] = useState(false);

  const fetchData = async () => {
    const sellerId = localStorage.getItem("sellerId");
    const token = localStorage.getItem("token");
    try {
      const [salesResponse, ordersResponse] = await Promise.all([
        axios.get(`${baseurl}/api/seller/sales/income/${sellerId}`),
        axios.get(`${baseurl}/api/order/seller-order`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setData(salesResponse.data);

      const orders = ordersResponse.data || [];
      setorders(orders);

      let pending = 0;
      let delivered = 0;
      orders.forEach((order) => {
        order.tracking.forEach((track) => {
          const status =
            track.statusUpdates[track.statusUpdates.length - 1]?.status;
          if (status === "Pending") pending++;
          else if (status === "Delivered") delivered++;
        });
      });

      setPendingCount(pending);
      setDeliveredCount(delivered);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
      seterror(true);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const stats = useMemo(
    () => [
      {
        icon: FaShoppingCart,
        title: "Total Income",
        value: `₹${data?.totalIncome?.toFixed(2) || "0.00"}`,
        iconColor: "text-blue-600",
      },
      {
        icon: FaBox,
        title: "Total Products Sold",
        value: data?.totalProductsSold,
        iconColor: "text-emerald-600",
      },
      {
        icon: FaHourglassHalf,
        title: "Pending Orders",
        value: pendingCount,
        iconColor: "text-amber-600",
      },
      {
        icon: FaTruck,
        title: "Delivered Orders",
        value: deliveredCount,
        iconColor: "text-rose-600",
      },
    ],
    [data, pendingCount, deliveredCount]
  );

  const displayName = root?.name
    ? root.name.charAt(0).toUpperCase() + root.name.slice(1)
    : "Seller";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Header */}
      <div className="sticky top-0 z-20 bg-white/85 backdrop-blur border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900">
              Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Track your sales activity, leads, and deals here.
            </p>
          </div>

          <div className="w-fit mt-2 sm:mt-0 inline-flex items-center rounded-full border bg-slate-50 px-3 py-1 text-sm font-extrabold text-slate-700">
            Welcome back,&nbsp;
            <span className="text-blue-700">{displayName}</span>!
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats Section */}
        <div className="rounded-2xl border bg-white shadow-sm p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <p className="text-sm font-extrabold text-slate-900">
                Overview
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                Quick snapshot of your store performance
              </p>
            </div>

            {!loading && !error && (
              <span className="inline-flex items-center rounded-full border bg-slate-50 px-3 py-1 text-xs font-extrabold text-slate-700">
                Updated just now
              </span>
            )}
          </div>

          {/* Mobile: Horizontal scroll cards */}
          <div className="sm:hidden">
            {loading ? (
              <div className="flex justify-center items-center min-h-[140px]">
                <Loader />
              </div>
            ) : error ? (
              <div className="flex justify-center items-center min-h-[140px]">
                <div className="rounded-2xl border bg-slate-50 px-4 py-5 text-center w-full">
                  <p className="text-sm font-extrabold text-slate-900">
                    No data yet
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Once you receive orders, they’ll show up here!
                  </p>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-4 px-4">
                <div className="flex gap-3 snap-x snap-mandatory">
                  {stats.map((s, idx) => (
                    <div
                      key={idx}
                      className="min-w-[78%] snap-start shrink-0 rounded-2xl border bg-white shadow-sm p-3"
                    >
                      <StatBox
                        icon={s.icon}
                        title={s.title}
                        value={s.value}
                        iconColor={s.iconColor}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Desktop/Tablet: Grid */}
          <div className="hidden sm:block">
            {loading ? (
              <div className="flex justify-center items-center min-h-[200px]">
                <Loader />
              </div>
            ) : error ? (
              <div className="flex justify-center items-center min-h-[200px]">
                <div className="rounded-2xl border bg-slate-50 px-6 py-6 text-center w-full">
                  <p className="text-base font-extrabold text-slate-900">
                    No data yet
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    Once you receive orders, they’ll show up here!
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
                {stats.map((s, idx) => (
                  <div
                    key={idx}
                    className="rounded-2xl border bg-white shadow-sm p-3"
                  >
                    <StatBox
                      icon={s.icon}
                      title={s.title}
                      value={s.value}
                      iconColor={s.iconColor}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
          {/* Sales Graph */}
          <div className="lg:col-span-2 rounded-2xl border bg-white shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b">
              <p className="text-sm font-extrabold text-slate-900">
                Sales Analytics
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                Track income trends and performance
              </p>
            </div>
            <div className="p-3 sm:p-4 h-[280px] sm:h-auto">
              <SalesGraph />
            </div>
          </div>

          {/* Low Stock */}
          <div className="rounded-2xl border bg-white shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div>
                <p className="text-sm font-extrabold text-slate-900">
                  Low Stock
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Products that need restocking
                </p>
              </div>
            </div>
            <div className="p-3 sm:p-4 max-h-[380px] overflow-auto">
              <LowStockTable />
            </div>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="mt-5 rounded-2xl border bg-white shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b">
            <p className="text-sm font-extrabold text-slate-900">
              Recent Orders
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              Latest updates from your order pipeline
            </p>
          </div>

          <div className="p-2 sm:p-4 overflow-x-auto">
            <RecentOrders orders={orders} error={error} loading={loading} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;